// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/formatToolStepBody.test.ts

function stringField(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

type IntentDraft = {
  area?: unknown;
  suspected_issue?: unknown;
  why_it_matters?: unknown;
  severity_guess?: unknown;
};

function formatIntentsFromDrafts(
  parsed: Record<string, unknown>,
): string | null {
  const intents = parsed.intents;
  if (!Array.isArray(intents) || intents.length === 0) return null;

  const lines: string[] = [
    intents.length === 1
      ? "1 suspected issue"
      : `${intents.length} suspected issues`,
  ];

  let shown = 0;
  for (const item of intents) {
    if (item == null || typeof item !== "object") continue;
    shown += 1;
    const intent = item as IntentDraft;
    const area = stringField(intent.area);
    const severity = stringField(intent.severity_guess);
    const issue = stringField(intent.suspected_issue);
    const why = stringField(intent.why_it_matters);

    const header = [area || "Issue", severity ? `(${severity})` : ""]
      .filter(Boolean)
      .join(" ");
    lines.push("");
    lines.push(`${shown}. ${header}`);
    if (issue) lines.push(`   ${issue}`);
    if (why) lines.push(`   Why it matters: ${why}`);
  }

  return shown > 0 ? lines.join("\n").trim() : null;
}

function formatSubagentLikeReport(
  parsed: Record<string, unknown>,
): string | null {
  const summary = stringField(parsed.summary);
  if (summary && !summary.startsWith("{") && !summary.startsWith("[")) {
    return summary;
  }
  const error = stringField(parsed.error);
  if (error) return error;
  const output = stringField(parsed.output);
  if (output) return output;
  const message = stringField(parsed.message);
  if (message) return message;
  const instructions = stringField(parsed.instructions);
  if (instructions) return instructions;
  const text = stringField(parsed.text);
  if (text) return text;
  return null;
}

function formatDraftCount(parsed: Record<string, unknown>): string | null {
  if (typeof parsed.drafts === "number") {
    const count = parsed.drafts;
    return count === 1 ? "1 draft" : `${count} drafts`;
  }
  return null;
}

function tryParseJson(text: string): unknown | null {
  const trimmed = text.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

/**
 * Turn a warhol tool_result summary into readable prose. Christo stores
 * structured returns as JSON strings; pretty-printing them in the accordion
 * body is worse than showing nothing.
 */
export function formatToolStepBody(text: string, tool?: string): string {
  const trimmed = text.trim();
  if (!trimmed) return "";

  const parsed = tryParseJson(trimmed);
  if (parsed == null) return trimmed;

  if (Array.isArray(parsed)) {
    const parts = parsed
      .map((entry) => {
        if (entry == null || typeof entry !== "object") return "";
        return formatSubagentLikeReport(entry as Record<string, unknown>) ?? "";
      })
      .filter(Boolean);
    if (parts.length > 0) return parts.join("\n\n");
    return "";
  }

  if (typeof parsed === "object" && parsed !== null) {
    const obj = parsed as Record<string, unknown>;

    if (tool === "intents_from_drafts" || obj.intents != null) {
      const formatted = formatIntentsFromDrafts(obj);
      if (formatted) return formatted;
    }

    const reportLike = formatSubagentLikeReport(obj);
    if (reportLike) return reportLike;

    const draftCount = formatDraftCount(obj);
    if (draftCount) return draftCount;

    for (const key of ["result", "content", "description"]) {
      const val = stringField(obj[key]);
      if (val) return val;
    }
  }

  return "";
}
